import Loading from 'components/Loading';
import useObjections from 'hooks/useObjections';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Form } from 'react-bootstrap';
import { useHistory, useLocation } from 'react-router-dom';
import { HashLink as Link } from 'react-router-hash-link';
import { InfoCircle } from 'react-bootstrap-icons';
import Select from 'components/Select';
import { ObjectionType, ObjectionVariant, Role } from 'briefpoint-client';
import { FormModal } from 'components/Modals/Modal';
import { useAuth } from 'hooks/useAuth';
import { VariantOption } from 'components/Select/Select';
import { isDeletable, userCanEditVariant } from 'utils/objectionUtils';

export default function AddEditObjection() {
  const [objections, updateVariant, createVariant, getDefaultObjection, deleteVariant] = useObjections();
  const location = useLocation();
  const initialType = new URLSearchParams(location.search).get('type');
  const isCopying = new URLSearchParams(location.search).get('copy');
  const { user } = useAuth()!;
  const [type, setType] = useState<number>();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [additionalContent, setAdditionalContent] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [toDefault, setToDefault] = useState(false);
  const [isSharedWithFirm, setIsSharedWithFirm] = useState<boolean | undefined>();
  const [objectionName, setObjectionName] = useState('');
  const [objectionType, setObjectionType] = useState<ObjectionType>(ObjectionType.Standard);
  const [error, setError] = useState('');
  const [showAlert, setShowAlert] = useState(true);
  const [deletingVariant, setDeletingVariant] = useState(false);
  const history = useHistory();
  const objectionVariant = location.state as ObjectionVariant;
  const isOwner = !objectionVariant || objectionVariant.ownerId === user?.id;
  const isChangingToFirm = !objectionVariant?.sharedWithFirmId && isSharedWithFirm;
  const allVariants = objections?.flatMap(x => x.variants);

  useEffect(() => {
    if (objectionVariant) {
      setType(objectionVariant.objectionId);
      setTitle(objectionVariant.name);
      setContent(objectionVariant.content);
      if (objectionVariant.additionalContent) {
        setAdditionalContent(objectionVariant.additionalContent);
      }
      if (!isCopying) {
        setIsDefault(objectionVariant.isDefault);
        setIsSharedWithFirm(!!objectionVariant.sharedWithFirmId ?? false)
        setToDefault(objectionVariant.isDefault);
      }
    }
    else if (initialType) {
      setType(parseInt(initialType));
    }
  }, [initialType, objectionVariant, isCopying]);

  useEffect(() => {
    if (objections && objectionVariant) {
      const objection = objections.find((objection) => objection.id === objectionVariant.objectionId);
      if (objection) {
        setObjectionName(objection.name);
        setObjectionType(objection.objectionType);
      }
    }
  }, [objectionVariant, objections]);

  useEffect(() => {
    if (objections && type) {
      const objection = objections.find((objection) => objection.id === type);
      if (objection) {
        setObjectionName(objection.name);
        setObjectionType(objection.objectionType);
        if (initialType) {
          setContent(objection.variants?.find(v => v.isDefault)?.content ?? '');
        }
      }
    }
  }, [objections, type, initialType]);

  const isResponseish = objectionType === ObjectionType.Response || objectionType === ObjectionType.NoResponse;

  function handleChangeContent(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setContent(event.target.value);
  }

  function handleChangeAdditionalContent(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setAdditionalContent(event.target.value);
  }

  function handleChangeTitle(event: React.ChangeEvent<HTMLInputElement>) {
    setTitle(event.target.value);
  }

  async function onSave() {
    let hasError = false;

    if (!title.trim()) {
      setError('Please provide a Title.');
      hasError = true;
    }

    if (!content.trim() && !additionalContent.trim()) {
      setError('Please provide some Content.');
      hasError = true;
    }

    if (!type) {
      setError('Please select a Type.');
      hasError = true;
    }
    if (!hasError && type) {
      if (objectionVariant === undefined || isCopying || (isSharedWithFirm && !user?.role?.includes(Role.FirmAdmin))) {
        //Add
        await createVariant(type, title, content, toDefault, additionalContent, isOwner && isSharedWithFirm ? user?.firmId! : undefined, false);
      } else {
        //Edit
        await updateVariant(objectionVariant.objectionId, objectionVariant.id, title, content, toDefault, additionalContent, isSharedWithFirm ? user?.firmId! : undefined, false);
      }
      history.goBack();
    }
  }

  async function onDelete() {
    if (objectionVariant !== null) {
      await deleteVariant(objectionVariant.objectionId, objectionVariant.id);
      history.goBack();
    }
  }

  async function onInsert() {
    if (!type) return;
    const response = await getDefaultObjection(type);
    if (response) {
      if (response.content) {
        setContent(response.content);
      }

      if (response.additionalContent) {
        setAdditionalContent(response.additionalContent);
      }
    }
  }

  function EditObjectionLink() {
    if (isResponseish) {
      return (
        <>
          <Link to="/library?tab=responses">Responses</Link> {` > Edit`}
        </>
      );
    }
    return (
      <>
        <Link to="/library?tab=objections">All Objections</Link> {` > Edit`}
      </>
    );
  }

  const options: VariantOption[] = useMemo(
    () =>
      objections != null
        ? objections.flatMap((v) => [
          {
            value: v.id!,
            label: v.name,
            key: ''
          },
        ])
        : [],
    [objections]
  );

  return (
    <>
      <Container className="page-container library-container">
        <div className="mb-4 bread-crumbs">
          <Link to={'/library'}>Library</Link> {'> '}
          {objectionVariant === undefined ? 'Create new' : EditObjectionLink()}
        </div>
        <h2>
          {objectionVariant === undefined ? 'Add' : (isCopying ? 'Copy' : 'Edit')} {isDefault ? 'default' : ''} {isResponseish ? 'response' : 'objection'}
        </h2>
        <Loading isLoading={!objections}>
          {error && <Alert className='col-8' variant="danger">{error}</Alert>}
          {showAlert && isOwner && (!isChangingToFirm && isSharedWithFirm && !isCopying) && <Alert className='col-8' onClose={() => setShowAlert(false)} dismissible variant='warning' >{'You are editing a Firm Standard usable by all firm members.'}</Alert>}
          {user?.role?.includes(Role.FirmAdmin) && (
            <>
              <span className='form-label'>Add to</span>
              <Form.Group>
                <Form.Check
                  id="existing-share-with-firm-no"
                  inline
                  type="radio"
                  label="Personal Library"
                  onChange={(e) => setIsSharedWithFirm(!e.target.checked)}
                  checked={!isSharedWithFirm}
                  disabled={objectionVariant && !!objectionVariant.sharedWithFirmId && !isCopying}
                />
                <Form.Check
                  id="existing-share-with-firm-yes"
                  type="radio"
                  inline
                  label="Firm Library"
                  onChange={(e) => setIsSharedWithFirm(e.target.checked)}
                  checked={isSharedWithFirm}
                  disabled={objectionVariant && !!objectionVariant.sharedWithFirmId && !isCopying}
                />
              </Form.Group>
            </>
          )}

          {objectionVariant ? (
            <h3 className='mt-2'>{objectionName}</h3>
          ) : (
            <Form.Group className="mt-3">
              <Form.Label>Type</Form.Label>
              <Select
                className="select objection-type-select"
                options={options}
                onChange={(value: number) => setType(value)}
                selected={type}
              />
            </Form.Group>
          )}
          <Form.Group className="title-group">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={title}
              className="font-times mb-2"
              style={{ maxWidth: 500 }}
              onChange={handleChangeTitle}
            />
          </Form.Group>
          <Form.Group>
            <div className="content-wrapper">
              <Form.Label htmlFor='content-box'>{objectionType === ObjectionType.Response ? 'Intro' : 'Content'}</Form.Label>
              <Button className="insert-button" variant="outline-primary" size='sm' onClick={onInsert} disabled={!type}>
                Insert Briefpoint Default
              </Button>
            </div>
            <Form.Control
              id="content-box"
              as="textarea"
              value={content}
              className="font-times mb-2"
              style={{ height: '100px', maxWidth: 700 }}
              onChange={handleChangeContent}
            />
          </Form.Group>
          {objectionType === ObjectionType.Response && (
            <Form.Group>
              <Form.Label>Response</Form.Label>
              <Form.Control
                as="textarea"
                value={additionalContent}
                className="font-times mb-2"
                style={{ height: '100px', maxWidth: 700 }}
                onChange={handleChangeAdditionalContent}
              />
            </Form.Group>
          )}
          <Form.Check
            id="existing-use-as-default"
            className="mt-2"
            type="checkbox"
            label={`Use as ${(userCanEditVariant(objectionVariant, user!) && isSharedWithFirm ? 'Firm' : 'Personal')} Default`}
            onChange={(e) => setToDefault(e.target.checked)}
            checked={toDefault}
          />
          <p className={'placeholder-link'}><InfoCircle className="mr-2" style={{ marginRight: 10 }} /><Link to="/how-to/#using_placeholders" target={'_blank'}>Learn how to customize your content with Placeholders.</Link></p>
          <div className="d-flex mt-5">
            <Button variant="primary" onClick={onSave}>
              {!isCopying ? 'Save' : 'Copy and Save'}
            </Button>
            <Button variant="link" onClick={() => history.goBack()}>
              Cancel
            </Button>
            {objectionVariant && isDeletable(objectionVariant, allVariants, user!) && (
              <Button onClick={() => setDeletingVariant(true)} className="delete-button" variant="link">
                Delete
              </Button>
            )}
          </div>
        </Loading>
      </Container>
      <FormModal
        title={'Are you sure?'}
        cancelText="Cancel"
        confirmText="Yes, Delete"
        show={deletingVariant}
        onClose={() => setDeletingVariant(false)}
        onConfirm={onDelete}
      >
        <div>{`Are you sure you want to delete this ${isResponseish ? 'response' : 'objection'} from your library? This cannot be undone.`}</div>
      </FormModal>
    </>
  );
}
